/**
 * IndexTrail — the concept map's ONE shared row-list primitive (TASK-016,
 * design pass 2026-07-22 after Daniele's verdict on the prior two attempts:
 * a force graph, then a "percorso di prerequisiti" with margin braces —
 * both bocciati). His words, verbatim: la Mappa è "un indice ragionato, non
 * un diagramma di relazioni" — capire di cosa parla il capitolo, poi saltare
 * a leggerlo. So this component draws NOTHING relational: no arcs, no
 * braces, no force layout. It draws a table of contents entry — number,
 * title, a summary that is ALWAYS visible (never behind hover/click) — and
 * nothing else. Both zoom levels of the map (capitoli, poi concetti di un
 * capitolo) are this SAME list at a different scope: MacroIndex.tsx and
 * ChapterOverview.tsx each build their own `TrailItem[]` and hand all
 * drawing to this component, so the two levels can never visually drift.
 *
 * Signature element — "il filo di lettura": a single hairline down the left
 * margin with a small tick at every row, one continuous line for the whole
 * list. It is the one thing about this content that is actually TRUE
 * structure (curriculum order / reading order — the only real signal left
 * after `examWeight` turned out to be zero on every macro/cross edge, see
 * the task doc's Core v1.2 remediation) — everything else in the old
 * designs (relation types, edge weight, force clusters) is gone.
 *
 * The resting-state mark is a short vertical TICK, not a circle: a filled
 * dot at rest reads as a graph node — exactly the vocabulary two rejections
 * asked to leave behind — while a tick reads as a stitch in a bound
 * manual's margin, which is what this product actually makes (`Arianna`
 * turns raw PDFs into "manuali"). It only becomes a circle, filled with
 * `--accent`, when its row is hovered/focused — the one accent moment in an
 * otherwise quiet, ink-on-paper list. That hover/focus is also the ONLY
 * accent color used anywhere in the row: the goal is "apertura di capitolo
 * di un manuale ben fatto", not a dashboard.
 *
 * Positioning is deliberately NOT computed (no per-row pixel math, no SVG
 * paths keyed to row heights) — the exact brittleness that broke the
 * previous two attempts on real data. The spine is one absolutely
 * positioned element stretched `top:0`/`bottom:0` inside a `position:
 * relative` list wrapper (so it always spans however tall the rows turn out
 * to be, blurbs of any length, no measurement); each row's tick is
 * positioned in ITS OWN row-local coordinate system, at a fixed offset from
 * the row's own top edge — never dependent on any other row's height.
 *
 * Motion: a spine that draws in (`scaleY` 0→1, `transformOrigin: top`) and
 * rows that reveal via `whileInView` (task doc: 12-14 rows per chapter,
 * most of them below the fold — a `whileInView` trigger doubles as BOTH the
 * mount-time entrance the task calls for and the "scroll reveal" for rows
 * further down, with one mechanism instead of two). `useReducedMotion`
 * degrades every transition to `duration: 0` — the row is still fully
 * there, still clickable, just not animated in.
 */
import type { CSSProperties } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import Heading from '@arianna/design-system/Heading'
import Text from '@arianna/design-system/Text'

/** Shared reading-width cap (task doc: "larghezza di lettura massima
 *  ~780-880px centrata su desktop") — exported so both MacroIndex and
 *  ChapterOverview size their OUTER container (header + trail together) to
 *  the same measure instead of each guessing their own number. */
export const TRAIL_MAX_WIDTH = 820

const TRAIL_STYLES = `
.concept-map-trail-row { outline: none; }
.concept-map-trail-row:hover,
.concept-map-trail-row:focus-visible {
  background: var(--surface-2);
}
.concept-map-trail-row:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: -2px;
  border-radius: var(--radius-sm);
}
.concept-map-trail-tick {
  width: 5px;
  height: 9px;
  border-radius: 2px;
  background: var(--line-2);
  transition: width 0.15s ease, height 0.15s ease, border-radius 0.15s ease,
    background-color 0.15s ease, box-shadow 0.15s ease;
}
.concept-map-trail-row:hover .concept-map-trail-tick,
.concept-map-trail-row:focus-visible .concept-map-trail-tick {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--accent);
  box-shadow: 0 0 0 4px var(--accent-soft);
}
.concept-map-trail-content {
  transition: transform 0.18s ease;
}
.concept-map-trail-row:hover .concept-map-trail-content,
.concept-map-trail-row:focus-visible .concept-map-trail-content {
  transform: translateX(6px);
}
.concept-map-trail-arrow {
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.concept-map-trail-row:hover .concept-map-trail-arrow,
.concept-map-trail-row:focus-visible .concept-map-trail-arrow {
  opacity: 1;
  transform: translateX(0);
}
@media (max-width: 480px) {
  .concept-map-trail { --trail-gutter: 26px; }
}
`

export interface TrailItem {
  id: string
  /** 1-based, shown as "01", "02"… — curriculum/reading order, the one
   *  genuine sequence in this data. */
  ordinal: number
  title: string
  /** Always rendered, never gated behind hover/click — the whole point of
   *  this redesign (task doc: "il blurb è il punto del redesign"). */
  blurb: string
  /** e.g. "3 blocchi" — optional, shown under the blurb when present. */
  meta?: string
}

export interface IndexTrailProps {
  items: readonly TrailItem[]
  onSelect: (id: string) => void
  /** Applied to every row's `<button>` — the regression contract
   *  (`concept-map-macro-node` / `concept-map-meso-node`). */
  itemTestId: string
  ariaLabel: string
  /** Seconds to hold before this trail's own entrance (spine draw + row
   *  reveal) begins — lets a caller with its own header sequence (eyebrow →
   *  title → summary) chain the trail in AFTER it, as one orchestrated
   *  moment instead of two competing ones. Default 0 (no header to wait
   *  for, e.g. the chapter list). */
  startDelay?: number
}

export function IndexTrail({
  items,
  onSelect,
  itemTestId,
  ariaLabel,
  startDelay = 0,
}: IndexTrailProps) {
  const reducedMotion = useReducedMotion()

  return (
    <div
      className="concept-map-trail"
      style={{ ['--trail-gutter' as string]: '40px', position: 'relative' } as CSSProperties}
    >
      <style>{TRAIL_STYLES}</style>
      <motion.div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 'var(--trail-gutter)',
          top: 0,
          bottom: 0,
          width: 1,
          background: 'var(--line)',
          transformOrigin: 'top',
        }}
        initial={reducedMotion ? false : { scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={
          reducedMotion ? { duration: 0 } : { duration: 0.5, delay: startDelay, ease: 'easeOut' }
        }
      />
      <div role="list" aria-label={ariaLabel} style={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((item, index) => (
          <TrailRow
            key={item.id}
            item={item}
            index={index}
            onSelect={onSelect}
            testId={itemTestId}
            reducedMotion={reducedMotion}
            startDelay={startDelay}
          />
        ))}
      </div>
    </div>
  )
}

interface TrailRowProps {
  item: TrailItem
  index: number
  onSelect: (id: string) => void
  testId: string
  reducedMotion: boolean | null
  startDelay: number
}

function TrailRow({ item, index, onSelect, testId, reducedMotion, startDelay }: TrailRowProps) {
  // Capped stagger: a `whileInView` trigger fires per-row as it individually
  // enters the viewport, so the stagger only visibly matters for rows that
  // are ALL on screen at once (the first handful, at mount) — capping it
  // keeps a row scrolled into view much later from waiting on a delay that
  // was only ever meant for its neighbours at the top.
  const rowDelay = startDelay + 0.15 + Math.min(index, 6) * 0.04

  return (
    <div role="listitem">
      <motion.button
        type="button"
        className="concept-map-trail-row"
        data-testid={testId}
        aria-label={`Apri ${item.title}`}
        onClick={() => onSelect(item.id)}
        style={{
          position: 'relative',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'flex-start',
          width: '100%',
          gap: 'var(--space-3)',
          padding: 'var(--space-4) var(--space-2) var(--space-4) 0',
          background: 'none',
          border: 'none',
          borderBottom: '1px solid var(--line)',
          cursor: 'pointer',
          textAlign: 'left',
          font: 'inherit',
        }}
        initial={reducedMotion ? false : { opacity: 0, x: -10 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={
          reducedMotion ? { duration: 0 } : { duration: 0.32, delay: rowDelay, ease: 'easeOut' }
        }
      >
        <span
          aria-hidden="true"
          style={{
            flex: '0 0 var(--trail-gutter)',
            display: 'flex',
            justifyContent: 'center',
            paddingTop: 2,
          }}
        >
          <Text variant="mono-xs" as="span">
            {String(item.ordinal).padStart(2, '0')}
          </Text>
        </span>
        <span
          className="concept-map-trail-tick"
          aria-hidden="true"
          style={{ position: 'absolute', left: 'calc(var(--trail-gutter) - 4px)', top: 20 }}
        />
        <span className="concept-map-trail-content" style={{ flex: '1 1 auto', minWidth: 0 }}>
          <Heading variant="cardSm" as="span" style={{ display: 'block', color: 'var(--ink)' }}>
            {item.title}
          </Heading>
          <Text variant="prose" as="span" style={{ display: 'block', marginTop: 4 }}>
            {item.blurb}
          </Text>
          {item.meta != null && item.meta.length > 0 && (
            <Text variant="meta" as="span" style={{ display: 'block', marginTop: 4 }}>
              {item.meta}
            </Text>
          )}
        </span>
        <span
          className="concept-map-trail-arrow"
          aria-hidden="true"
          style={{
            flex: '0 0 auto',
            alignSelf: 'center',
            color: 'var(--accent)',
            fontFamily: 'var(--font-mono)',
            fontSize: 14,
          }}
        >
          →
        </span>
      </motion.button>
    </div>
  )
}
